import jwtDecode from 'jwt-decode';
import {
  isAndroid,
  isIOS,
  mobileVendor,
  mobileModel,
  osName,
  osVersion,
  browserName,
  fullBrowserVersion
} from 'react-device-detect';
import http from 'src/http/fetchClient';
import CONTENT_TYPES from './contentTypes';
import { getToken, setToken } from 'src/utils/tokenStore';

class AuthService {
  setAxiosInterceptors = ({ onLogout }) => {
    http.setOnUnauthorized(() => {
      if (!this.getAccessToken()) return;
      this.setSession(null);
      if (onLogout) {
        onLogout();
      }
    });
  };

  handleAuthentication() {
    localStorage.removeItem('accessToken'); // migration cleanup: remove token persisted by old implementation
  }

  loginWithEmailAndPassword = (email, password) =>
    new Promise((resolve, reject) => {
      http
        .post(
          '/api/admin/accounts/login',
          {
            email,
            password,
            // eslint-disable-next-line no-nested-ternary
            platform: isAndroid === true ? 'AND' : isIOS === true ? 'IOS' : 'WEB',
            manufacturer: mobileVendor,
            model: mobileModel,
            osVersion: `${osName} ${osVersion}`,
            browserName,
            browserVersion: fullBrowserVersion
          },
          {
            headers: {
              'Content-Type': CONTENT_TYPES.accounts_authentication
            }
          }
        )
        .then((response) => {
          if (response.data.authToken) {
            this.setSession(response.data.authToken);
            resolve({
              ...response.data.profile,
              ...{ passwordExpired: response.data.passwordExpired }
            });
          } else if (response.data.mfa) {
            resolve({ mfa: response.data.mfa });
          } else {
            reject(new Error(response.data?.errorMessage || 'Login failed'));
          }
        })
        .catch((error) => {
          const loginError = new Error(error.response?.data?.errorMessage || error.message || 'Login failed');
          loginError.errorCode = error.response?.data?.errorCode;
          reject(loginError);
        });
    });

  loginInWithToken = () =>
    new Promise((resolve, reject) => {
      if (sessionStorage.getItem('loggedOut')) {
        reject(new Error('Session ended'));
        return;
      }
      http
        .post(
          '/api/admin/accounts/session',
          {},
          {
            headers: {
              'Content-Type': CONTENT_TYPES.accounts_authentication
            }
          }
        )
        .then((response) => {
          if (response.data.authToken) {
            this.setSession(response.data.authToken);
            resolve({
              ...response.data.profile,
              ...{ passwordExpired: response.data.passwordExpired }
            });
          } else {
            this.setSession(null);
            reject(new Error('Session verification failed'));
          }
        })
        .catch((error) => {
          this.setSession(null);
          reject(error);
        });
    });

  logout = () => {
    const token = getToken();
    sessionStorage.setItem('loggedOut', '1');
    this.setSession(null);
    if (token) {
      http
        .post(
          '/api/admin/accounts/logout',
          {},
          {
            headers: { 'Content-Type': CONTENT_TYPES.accounts_authentication }
          }
        )
        .catch(() => {}); // intentional: UX must not block on logout failure
    }
  };

  setSession = (accessToken) => {
    setToken(accessToken || null);
    if (accessToken) {
      sessionStorage.removeItem('loggedOut');
    } else {
      localStorage.removeItem('activeProviderId');
      localStorage.removeItem('activePartnerId');
      localStorage.removeItem('activeDistributorId');
    }
  };

  getAccessToken = () => getToken();

  forgotPassword = async (email) => {
    try {
      return await http.get('/api/admin/accounts/password/forgot', {
        headers: {
          'Content-Type': CONTENT_TYPES.accounts_authentication
        },
        data: {},
        params: { email }
      });
    } catch (error) {
      return error.response;
    }
  };

  completeRegistration = async (email, password, hash) => {
    try {
      const response = await http.post(
        '/api/admin/accounts/invite/accept',
        {
          email,
          password,
          hash,
          // eslint-disable-next-line no-nested-ternary
          platform: isAndroid === true ? 'AND' : isIOS === true ? 'IOS' : 'WEB',
          manufacturer: mobileVendor,
          model: mobileModel,
          osVersion: `${osName} ${osVersion}`,
          browserName,
          browserVersion: fullBrowserVersion
        },
        {
          headers: {
            'Content-Type': CONTENT_TYPES.accounts_authentication
          }
        }
      );
      if (response.data.authToken) {
        this.setSession(response.data.authToken);
      }
      return response;
    } catch (error) {
      return error.response;
    }
  };

  getRolePermissions = async (roleId) => {
    const response = await http.get(`/api/admin/accounts/roles/${roleId}`, {
      headers: {
        'Content-Type': CONTENT_TYPES.accounts_role
      },
      data: {}
    });

    return response.data;
  };

  resetPassword = async (password, hash) => {
    try {
      const response = await http.put(
        '/api/admin/accounts/password/forgot',
        {
          password,
          hash,
          // eslint-disable-next-line no-nested-ternary
          platform: isAndroid === true ? 'AND' : isIOS === true ? 'IOS' : 'WEB',
          manufacturer: mobileVendor,
          model: mobileModel,
          osVersion: `${osName} ${osVersion}`,
          browserName,
          browserVersion: fullBrowserVersion
        },
        {
          headers: {
            'Content-Type': CONTENT_TYPES.accounts_authentication
          }
        }
      );
      if (response.data.authToken) {
        this.setSession(response.data.authToken);
      }
      return response;
    } catch (error) {
      return error.response;
    }
  };

  isValidToken = (accessToken) => {
    if (!accessToken) {
      return false;
    }

    const decoded = jwtDecode(accessToken);
    const currentTime = Date.now() / 1000;

    return decoded.exp > currentTime;
  };

  verifyPassword = async (password) => {
    const response = await http.post(
      '/api/admin/accounts/login/verify',
      { password },
      {
        headers: {
          'Content-Type': CONTENT_TYPES.accounts_authentication
        }
      }
    );

    return response.data;
  };

  isAuthenticated = () => !!this.getAccessToken();

  registerMFAToken = (token, trustDevice, sessionKey) =>
    new Promise((resolve, reject) => {
      http
        .post(
          '/api/admin/accounts/mfa/register',
          {
            token,
            trustDevice
          },
          {
            headers: {
              'Content-Type': CONTENT_TYPES.accounts_authentication,
              'X-SESSION-KEY': sessionKey
            }
          }
        )
        .then((response) => {
          if (response.data.authToken) {
            this.setSession(response.data.authToken);
            resolve({
              ...response.data.profile,
              ...{ passwordExpired: response.data.passwordExpired }
            });
          } else if (response.data.mfa) {
            const mfaError = new Error(
              response.data.mfa.error?.errorMessage || 'MFA registration was unsuccessful. Please try again.'
            );
            mfaError.mfa = response.data.mfa;
            reject(mfaError);
          } else {
            reject(new Error(response.data?.errorMessage || 'MFA registration failed'));
          }
        })
        .catch((error) => {
          const mfaData = error.response?.data?.mfa;
          const errorMessage =
            mfaData?.error?.errorMessage ||
            error.response?.data?.errorMessage ||
            error.message ||
            'MFA registration failed';
          const mfaError = new Error(errorMessage);
          if (mfaData) {
            mfaError.mfa = mfaData;
          }
          reject(mfaError);
        });
    });

  verifyMFAToken = (token, trustDevice, sessionKey) =>
    new Promise((resolve, reject) => {
      http
        .post(
          '/api/admin/accounts/mfa/verify',
          {
            token,
            trustDevice
          },
          {
            headers: {
              'Content-Type': CONTENT_TYPES.accounts_authentication,
              'X-SESSION-KEY': sessionKey
            }
          }
        )
        .then((response) => {
          if (response.data.authToken) {
            this.setSession(response.data.authToken);
            resolve({
              ...response.data.profile,
              ...{ passwordExpired: response.data.passwordExpired }
            });
          } else if (response.data.mfa) {
            const mfaError = new Error(
              response.data.mfa.error?.errorMessage || 'MFA verification was unsuccessful. Please try again.'
            );
            mfaError.mfa = response.data.mfa;
            reject(mfaError);
          } else {
            reject(new Error(response.data?.errorMessage || 'MFA verification failed'));
          }
        })
        .catch((error) => {
          const mfaData = error.response?.data?.mfa;
          const errorMessage =
            mfaData?.error?.errorMessage ||
            error.response?.data?.errorMessage ||
            error.message ||
            'MFA verification failed';
          const mfaError = new Error(errorMessage);
          if (mfaData) {
            mfaError.mfa = mfaData;
          }
          reject(mfaError);
        });
    });
}

const authService = new AuthService();

export default authService;
